import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Shield, ShieldCheck, ShieldOff, Copy, Check, KeyRound, Smartphone } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SecuritySettings = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [setupData, setSetupData] = useState(null); // {secret, qr_code, backup_codes, provisioning_uri}
  const [code, setCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [copied, setCopied] = useState('');

  const loadStatus = async () => {
    try {
      const res = await axios.get(`${API}/auth/2fa/status`);
      setStatus(res.data);
    } catch (e) {
      toast.error('Failed to load 2FA status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 1500);
  };

  const startSetup = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/auth/2fa/setup`);
      setSetupData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start 2FA setup');
    } finally {
      setBusy(false);
    }
  };

  const confirmSetup = async () => {
    if (!code.trim()) { toast.error('Enter the 6-digit code'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/2fa/verify`, { code: code.trim() });
      toast.success('Two-factor authentication enabled');
      setSetupData(null);
      setCode('');
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code');
    } finally {
      setBusy(false);
    }
  };

  const disable2FA = async () => {
    if (!disableCode.trim()) { toast.error('Enter your current code to disable'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/2fa/disable`, { code: disableCode.trim() });
      toast.success('Two-factor authentication disabled');
      setDisableCode('');
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center" data-testid="security-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  const enabled = status?.enabled;

  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto space-y-6" data-testid="security-settings-page">
      <div className="flex items-center gap-3">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-3">
          <Shield className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Security &amp; Two-Factor Authentication</h1>
          <p className="text-slate-500 text-sm">Add an extra layer of protection to your account with an authenticator app.</p>
        </div>
      </div>

      <Card className="shadow-soft">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-slate-600" />
              <CardTitle className="text-lg">Authenticator App (TOTP)</CardTitle>
            </div>
            {enabled ? (
              <Badge className="bg-green-100 text-green-700 hover:bg-green-100" data-testid="twofa-status-enabled">
                <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Enabled
              </Badge>
            ) : (
              <Badge variant="secondary" data-testid="twofa-status-disabled">
                <ShieldOff className="h-3.5 w-3.5 mr-1" /> Disabled
              </Badge>
            )}
          </div>
          <CardDescription>
            {enabled
              ? `Two-factor authentication is active. Backup codes remaining: ${status?.backup_codes_remaining ?? 0}.`
              : 'Use Google Authenticator, Authy, or any TOTP app to generate one-time codes.'}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-5">
          {/* DISABLED — show enable flow */}
          {!enabled && !setupData && (
            <Button onClick={startSetup} disabled={busy} className="btn-primary" data-testid="enable-2fa-button">
              <KeyRound className="h-4 w-4 mr-2" />
              {busy ? 'Preparing…' : 'Enable Two-Factor Authentication'}
            </Button>
          )}

          {/* SETUP IN PROGRESS */}
          {!enabled && setupData && (
            <div className="space-y-5" data-testid="twofa-setup-panel">
              <div className="grid md:grid-cols-2 gap-6 items-start">
                <div className="text-center">
                  <p className="text-sm font-medium text-slate-700 mb-2">1. Scan this QR code</p>
                  <img
                    src={setupData.qr_code}
                    alt="2FA QR code"
                    className="mx-auto border rounded-lg w-44 h-44"
                    data-testid="twofa-qr-image"
                  />
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-700">Or enter this key manually</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs bg-slate-100 rounded px-2 py-2 break-all" data-testid="twofa-secret">
                      {setupData.secret}
                    </code>
                    <Button type="button" variant="outline" size="icon" onClick={() => copyToClipboard(setupData.secret, 'secret')} data-testid="copy-secret-button">
                      {copied === 'secret' ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>

                  <p className="text-sm font-medium text-slate-700 pt-2">Backup codes (store safely)</p>
                  <div className="grid grid-cols-2 gap-1.5 bg-amber-50 border border-amber-200 rounded p-2" data-testid="twofa-backup-codes">
                    {setupData.backup_codes.map((bc) => (
                      <code key={bc} className="text-xs font-mono text-amber-900">{bc}</code>
                    ))}
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={() => copyToClipboard(setupData.backup_codes.join('\n'), 'codes')} data-testid="copy-backup-codes-button">
                    {copied === 'codes' ? <><Check className="h-4 w-4 mr-1 text-green-600" /> Copied</> : <><Copy className="h-4 w-4 mr-1" /> Copy all codes</>}
                  </Button>
                </div>
              </div>

              <div className="space-y-2 border-t pt-4">
                <Label htmlFor="verify-code">2. Enter the 6-digit code to confirm</Label>
                <div className="flex gap-2 max-w-xs">
                  <Input
                    id="verify-code"
                    data-testid="twofa-verify-input"
                    placeholder="123456"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className="text-center tracking-[0.3em]"
                  />
                  <Button onClick={confirmSetup} disabled={busy} className="btn-primary" data-testid="confirm-2fa-button">
                    {busy ? 'Verifying…' : 'Confirm'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* ENABLED — show disable flow */}
          {enabled && (
            <div className="space-y-3" data-testid="twofa-disable-panel">
              <Alert>
                <AlertDescription className="text-sm">
                  To disable 2FA, enter a current code from your authenticator app (or a backup code).
                </AlertDescription>
              </Alert>
              <div className="flex gap-2 max-w-xs">
                <Input
                  data-testid="disable-2fa-input"
                  placeholder="123456"
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value)}
                  className="text-center tracking-[0.3em]"
                />
                <Button variant="destructive" onClick={disable2FA} disabled={busy} data-testid="disable-2fa-button">
                  {busy ? 'Working…' : 'Disable 2FA'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SecuritySettings;
